export interface IntentState {
  readonly aimAngle: number;

  readonly moving: boolean;

  readonly firing: boolean;

  readonly braking: boolean;

  readonly pointerWorldX: number;
  readonly pointerWorldY: number;

  readonly panAxes: readonly [boolean, boolean, boolean, boolean];
}

export const IDLE_INTENT_STATE: IntentState = {
  aimAngle: 0,
  moving: false,
  firing: false,
  braking: false,
  pointerWorldX: 0,
  pointerWorldY: 0,
  panAxes: [false, false, false, false],
};

/** Deliberately not packets: turning an intent into a packet needs gating (fire rate, cooldowns, affordability) the input layer must not know about. */
export type Intent =
  | { readonly kind: 'fireOnce' }
  | { readonly kind: 'buyUpgrade'; readonly path: 0 | 1 | 2 }
  | { readonly kind: 'toggleUpgradeScreen' }
  | { readonly kind: 'evolve'; readonly path: number }
  | { readonly kind: 'useAbilityAt'; readonly worldX: number; readonly worldY: number }
  | { readonly kind: 'useAbilityOn'; readonly entityId: number }
  | { readonly kind: 'sendEmoji'; readonly index: number }
  | { readonly kind: 'suicide' }
  | { readonly kind: 'leaveMatch' }
  | { readonly kind: 'surrenderVote'; readonly agree: boolean }
  | { readonly kind: 'sendChat'; readonly text: string }
  | {
      readonly kind: 'zoomView';
      readonly steps: number;
      readonly deviceX: number;
      readonly deviceY: number;
    }
  | { readonly kind: 'pickFollow'; readonly worldX: number; readonly worldY: number }
  | { readonly kind: 'seekView'; readonly worldX: number; readonly worldY: number };

export interface InputSource {
  sample(context: InputContext): IntentState;
  contributes?(): boolean;
  drain(): Intent[];
  dispose(): void;
}

export interface InputContext {
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly worldScale: number;
  readonly cameraX: number;
  readonly cameraY: number;
  readonly spectating: boolean;
  /** A pointer event reports CSS pixels; every other field here is device pixels — mixing the two pins the aim angle to one quadrant. */
  readonly pixelRatio: number;
}

export class CompositeInputSource implements InputSource {
  constructor(private readonly sources: readonly InputSource[]) {}

  sample(context: InputContext): IntentState {
    let state = IDLE_INTENT_STATE;
    for (const source of this.sources) {
      const next = source.sample(context);
      if (source.contributes?.() === false) continue;
      state = {
        firing: state.firing || next.firing,
        moving: state.moving || next.moving,
        braking: state.braking || next.braking,
        panAxes: [
          state.panAxes[0] || next.panAxes[0],
          state.panAxes[1] || next.panAxes[1],
          state.panAxes[2] || next.panAxes[2],
          state.panAxes[3] || next.panAxes[3],
        ],
        aimAngle: next.aimAngle,
        pointerWorldX: next.pointerWorldX,
        pointerWorldY: next.pointerWorldY,
      };
    }
    return state;
  }

  drain(): Intent[] {
    const intents: Intent[] = [];
    for (const source of this.sources) intents.push(...source.drain());
    return intents;
  }

  dispose(): void {
    for (const source of this.sources) source.dispose();
  }
}

export function toWorld(
  clientX: number,
  clientY: number,
  context: InputContext,
): { x: number; y: number } {
  return {
    x: (clientX - context.viewportWidth / 2) / context.worldScale + context.cameraX,
    y: (clientY - context.viewportHeight / 2) / context.worldScale + context.cameraY,
  };
}
